import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { FormModal } from '../../../components/FormModal'
import { Icon } from '../../../components/Icon'
import { LoadingBlock } from '../../../components/Loading'
import { MoneyInput } from '../../../components/MaskedInput'
import { Tooltip } from '../../../components/Tooltip'
import { useHasRole, useAppToast } from '../../../helpers'
import EventCostService from '../../../services/EventCosts/EventCostService'
import { IEventCost } from '../../../services/EventCosts/interfaces'

type CostRow = {
  localId: string
  id: number | null
  description: string
  amount: number | null
  notes: string
  saved: { description: string; amount: number | null; notes: string }
}

const costService = new EventCostService()
const costFieldOrder: Array<'description' | 'amount' | 'notes'> = ['description', 'amount', 'notes']

let costDraftSeq = 0
function newCostDraft(): CostRow {
  costDraftSeq += 1
  return {
    localId: `draft-${costDraftSeq}-${Date.now()}`,
    id: null,
    description: '',
    amount: null,
    notes: '',
    saved: { description: '', amount: null, notes: '' }
  }
}

function mapCostToRow(cost: IEventCost): CostRow {
  const amount = cost.amount != null ? Number(cost.amount) : null
  return {
    localId: `cost-${cost.id}`,
    id: cost.id,
    description: cost.description ?? '',
    amount,
    notes: cost.notes ?? '',
    saved: {
      description: cost.description ?? '',
      amount,
      notes: cost.notes ?? ''
    }
  }
}

function computeCostsTotal(rows: CostRow[]): number {
  return rows.reduce((sum, r) => (r.id != null && r.saved.amount != null ? sum + r.saved.amount : sum), 0)
}

export default function EventCostsSectionModal({
  isOpen,
  onClose,
  eventId,
  onTotalChange
}: {
  isOpen: boolean
  onClose: () => void
  eventId: number
  onTotalChange: (total: number) => void
}) {
  const toast = useAppToast()
  const hasRole = useHasRole()
  const canAdd = hasRole('add_event_costs')
  const canEdit = hasRole('edit_event_costs')
  const canDelete = hasRole('delete_event_costs')

  const [rows, setRows] = useState<CostRow[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [invalidAmounts, setInvalidAmounts] = useState<Set<string>>(new Set())
  const rowsRef = useRef<CostRow[]>([])
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map())

  const updateRows = (updater: CostRow[] | ((prev: CostRow[]) => CostRow[])) => {
    setRows((prev) => {
      const next = typeof updater === 'function' ? (updater as (p: CostRow[]) => CostRow[])(prev) : updater
      rowsRef.current = next
      return next
    })
  }

  function fetchCosts() {
    setLoading(true)
    setLoadError(false)
    setInvalidAmounts(new Set())
    costService
      .list(eventId)
      .then((res) => {
        const mapped = res.data.map(mapCostToRow)
        updateRows(canAdd ? [...mapped, newCostDraft()] : mapped)
        onTotalChange(Number(res.total) || 0)
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (isOpen) fetchCosts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, eventId])

  function handleFieldChange(
    localId: string,
    field: 'description' | 'amount' | 'notes',
    value: string | number | null
  ) {
    if (field === 'amount' && value != null) {
      setInvalidAmounts((prev) => {
        if (!prev.has(localId)) return prev
        const next = new Set(prev)
        next.delete(localId)
        return next
      })
    }
    updateRows((prev) => {
      const updated = prev.map((r) => (r.localId === localId ? { ...r, [field]: value } : r))
      const changedRow = updated.find((r) => r.localId === localId)
      const isDraft = changedRow?.id == null
      const hasContent = !!(changedRow?.description || changedRow?.notes) || changedRow?.amount != null
      const lastRow = updated[updated.length - 1]
      const hasEmptyDraftAtEnd =
        lastRow?.id == null && !lastRow?.description && lastRow?.amount == null && !lastRow?.notes
      if (canAdd && isDraft && hasContent && !hasEmptyDraftAtEnd) {
        return [...updated, newCostDraft()]
      }
      return updated
    })
  }

  function focusNext(localId: string, field: 'description' | 'amount' | 'notes') {
    const current = rowsRef.current
    const fieldIdx = costFieldOrder.indexOf(field)
    if (fieldIdx < costFieldOrder.length - 1) {
      inputRefs.current.get(`${localId}:${costFieldOrder[fieldIdx + 1]}`)?.focus()
      return
    }
    const rowIdx = current.findIndex((r) => r.localId === localId)
    const nextRow = current[rowIdx + 1]
    if (nextRow) {
      inputRefs.current.get(`${nextRow.localId}:description`)?.focus()
    }
  }

  function handleKeyDown(
    e: React.KeyboardEvent<HTMLInputElement>,
    localId: string,
    field: 'description' | 'amount' | 'notes'
  ) {
    if (e.key === 'Enter') {
      e.preventDefault()
      focusNext(localId, field)
    }
  }

  async function handleDelete(localId: string) {
    const row = rowsRef.current.find((r) => r.localId === localId)
    if (!row) return

    if (row.id == null) {
      updateRows((prev) => {
        const drafts = prev.filter((r) => r.id == null)
        if (drafts.length > 1) return prev.filter((r) => r.localId !== localId)
        return prev.map((r) => (r.localId === localId ? { ...r, description: '', amount: null, notes: '' } : r))
      })
      return
    }

    if (!canDelete) return

    try {
      await costService.delete(eventId, row.id)
      const nextRows = rowsRef.current.filter((r) => r.localId !== localId)
      updateRows(nextRows)
      onTotalChange(computeCostsTotal(nextRows))
      toast.success('Custo removido')
    } catch {
      toast.error('Erro ao remover custo. Verifique a conexão.')
    }
  }

  async function handleSave() {
    const invalid = new Set<string>()
    rowsRef.current.forEach((row) => {
      if (row.id == null && canAdd) {
        const hasContent = !!(row.description.trim() || row.notes.trim())
        const hasAmount = !!row.amount
        if (hasContent && !hasAmount) invalid.add(row.localId)
      }
    })
    if (invalid.size > 0) {
      setInvalidAmounts(invalid)
      const firstInvalid = invalid.values().next().value
      inputRefs.current.get(`${firstInvalid}:amount`)?.focus()
      toast.warning('Preencha o valor de todos os custos antes de salvar.')
      return
    }

    const costs = rowsRef.current.flatMap((row) => {
      if (row.id == null) {
        if (!canAdd) return []
        const description = row.description.trim()
        const amount = row.amount
        const notes = row.notes.trim()
        if (!description && amount == null && !notes) return []
        return [{ description: description || null, amount, notes: notes || null }]
      }

      if (!canEdit) return []
      const description = row.description.trim()
      const amount = row.amount
      const notes = row.notes.trim()
      const changed = description !== row.saved.description || amount !== row.saved.amount || notes !== row.saved.notes
      if (!changed) return []
      return [{ id: row.id, description: description || null, amount, notes: notes || null }]
    })

    if (costs.length === 0) {
      onClose()
      return
    }

    setIsSaving(true)
    try {
      const res = await costService.sync(eventId, costs)
      const mapped = res.data.map(mapCostToRow)
      updateRows(canAdd ? [...mapped, newCostDraft()] : mapped)
      onTotalChange(res.total)
      toast.success('Custos salvos')
      onClose()
    } catch {
      toast.error('Erro ao salvar custos. Verifique a conexão.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Custos"
      size="wide"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} isDisabled={isSaving}>
            {canAdd || canEdit ? 'Cancelar' : 'Fechar'}
          </Button>
          {(canAdd || canEdit) && (
            <Button colorScheme="pink" onClick={handleSave} isLoading={isSaving} isDisabled={loading || loadError}>
              Salvar
            </Button>
          )}
        </>
      }
    >
      <Box>
        {loading ? (
          <LoadingBlock entity="custos" />
        ) : loadError ? (
          <Flex align="center" justify="center" direction="column" gap={3} py={8}>
            <Text variant="caption">Não foi possível carregar os custos.</Text>
            <Button size="sm" onClick={fetchCosts}>
              Tentar novamente
            </Button>
          </Flex>
        ) : rows.filter((r) => r.id != null).length === 0 && !canAdd ? (
          <Flex align="center" justify="center" py={8}>
            <Text variant="caption">Nenhum custo cadastrado.</Text>
          </Flex>
        ) : (
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th>Descrição</Th>
                  <Th w="180px">Valor (R$)</Th>
                  <Th>Observações</Th>
                  <Th w="48px" />
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row) => {
                  const readOnly = row.id != null ? !canEdit : !canAdd
                  return (
                    <Tr key={row.localId}>
                      <Td>
                        <Input
                          size="sm"
                          placeholder="Descrição do custo"
                          value={row.description}
                          isReadOnly={readOnly}
                          ref={(el) => {
                            const key = `${row.localId}:description`
                            if (el) inputRefs.current.set(key, el)
                            else inputRefs.current.delete(key)
                          }}
                          onChange={(e) => handleFieldChange(row.localId, 'description', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, row.localId, 'description')}
                        />
                      </Td>
                      <Td>
                        <MoneyInput
                          size="sm"
                          textAlign="right"
                          value={row.amount}
                          isReadOnly={readOnly}
                          isInvalid={invalidAmounts.has(row.localId)}
                          ref={(el) => {
                            const key = `${row.localId}:amount`
                            if (el) inputRefs.current.set(key, el)
                            else inputRefs.current.delete(key)
                          }}
                          onChange={(amount) => handleFieldChange(row.localId, 'amount', amount)}
                          onKeyDown={(e) => handleKeyDown(e, row.localId, 'amount')}
                        />
                      </Td>
                      <Td>
                        <Input
                          size="sm"
                          placeholder="Observações (opcional)"
                          value={row.notes}
                          isReadOnly={readOnly}
                          ref={(el) => {
                            const key = `${row.localId}:notes`
                            if (el) inputRefs.current.set(key, el)
                            else inputRefs.current.delete(key)
                          }}
                          onChange={(e) => handleFieldChange(row.localId, 'notes', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, row.localId, 'notes')}
                        />
                      </Td>
                      <Td>
                        {row.id != null && canDelete && (
                          <Flex align="center" justify="center">
                            <Tooltip label="Remover custo">
                              <IconButton
                                aria-label="Remover custo"
                                icon={<Icon name="lni lni-trash-can" size="md" />}
                                size="xs"
                                variant="ghost"
                                color="gray.400"
                                _hover={{ color: 'red.500' }}
                                onClick={() => handleDelete(row.localId)}
                              />
                            </Tooltip>
                          </Flex>
                        )}
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </FormModal>
  )
}
